import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MarkdownRenderer from '../components/MarkdownRenderer';
import {
  getExamQuestionState,
  getExamQuestions,
  postEndExam,
  postExamNavigate,
  postStartExam,
} from '../api/tutor';
import { formatSessionLabel, formatState } from '../lib/format';
import { useReportIssueContext } from '../reportIssueContext';

// The exam question comes from POST /sessions/{id}/exam/navigate, whose
// QuestionOut carries no family_key / skill_code / micro_skill_code: it exposes
// display *names* and does not populate those. Those keys live in
// generator_metadata_json, which the handler does return. Reading them straight
// off `question` compiles and silently yields null on every exam report, which
// is worse than sending nothing. Exported so the mapping is testable without a
// full ExamSession render harness.
export function buildExamReportContext({ sessionId, attemptId, question }) {
  const meta = question?.generator_metadata_json;
  return {
    session_id: sessionId ? Number(sessionId) || sessionId : null,
    attempt_id: attemptId || null,
    question_id: question?.id ?? null,
    family_key: meta?.family_key || null,
    skill_code: meta?.skill_code || null,
    micro_skill_code: meta?.micro_skill_code || null,
  };
}

function normalizeComparableText(markdown) {
  return String(markdown || '')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/!\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/[`*_>#~|-]/g, ' ')
    .replace(/\$+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function isBareInstructionLabel(normalizedStem) {
  return /^(?:calculate|choose|compare|complete|construct|convert|describe|determine|draw|estimate|evaluate|expand|explain|express|factorise|factorize|find|give|identify|measure|order|plot|prove|rearrange|round|select|show|simplify|solve|state|work out|write)\b/.test(
    normalizedStem
  );
}

function shouldRenderSharedQuestionStem({ stemMarkdown, firstPartPrompt }) {
  if (!String(stemMarkdown || '').trim()) {
    return false;
  }

  const normalizedStem = normalizeComparableText(stemMarkdown);
  const normalizedPrompt = normalizeComparableText(firstPartPrompt);
  if (!normalizedStem || !normalizedPrompt || normalizedStem === normalizedPrompt) {
    return false;
  }

  const stemRaw = String(stemMarkdown || '').trim();
  const hasStructuredContext =
    /\n\s*\n/.test(stemRaw) ||
    /!\[.*?\]\(.*?\)|\|.*\||^\s*[-*]\s+/m.test(stemRaw) ||
    normalizedStem.length > 120 ||
    /[.?!].+[.?!]/.test(normalizedStem);

  return hasStructuredContext || !isBareInstructionLabel(normalizedStem);
}

function ExamSessionPage() {
  const { sessionId } = useParams();
  const [orderIndex, setOrderIndex] = useState(1);
  const [paperQuestions, setPaperQuestions] = useState([]);
  const [paperLoading, setPaperLoading] = useState(false);
  const [paperError, setPaperError] = useState(null);
  const [question, setQuestion] = useState(null);
  const [attemptId, setAttemptId] = useState('');
  const [workspaceState, setWorkspaceState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');
  const [rangeMessage, setRangeMessage] = useState('');
  const [jumpValue, setJumpValue] = useState('1');
  const [isEnded, setIsEnded] = useState(false);
  const [endExamError, setEndExamError] = useState('');
  const [isEndingExam, setIsEndingExam] = useState(false);
  const navigate = useNavigate();
  const { setReportIssueContext } = useReportIssueContext();

  useEffect(() => {
    setReportIssueContext(buildExamReportContext({ sessionId, attemptId, question }));
    return () => setReportIssueContext({});
    // `question` whole, not its fields: the mapping reads several keys off it
    // (including nested generator_metadata_json), so listing the leaves both
    // trips react-hooks/exhaustive-deps and silently goes stale if the mapping
    // later reads a key the array does not name. `question` is state set once
    // per navigation, so its identity is stable between questions.
  }, [attemptId, question, sessionId, setReportIssueContext]);

  const handleGuardianVerificationBlock = useCallback(async (response) => {
    if (response?.status !== 403) {
      return false;
    }
    let data = null;
    try {
      const readable = typeof response.clone === 'function' ? response.clone() : response;
      data = await readable.json();
    } catch (_error) {
      data = null;
    }
    if (data?.detail === 'guardian_verification_required') {
      navigate('/access-restricted?reason=guardian', { replace: true });
      return true;
    }
    return false;
  }, [navigate]);

  const buildErrorMessage = async (response, fallback) => {
    const status = response?.status;
    let detail = '';

    if (response) {
      try {
        const data = await response.json();
        detail = data?.detail || data?.message || '';
      } catch (error) {
        detail = '';
      }
    }

    const detailSuffix = detail ? ` - ${detail}` : '';
    return `${fallback} (status ${status ?? 'unknown'})${detailSuffix}`;
  };

  const handleEndExam = async () => {
    setEndExamError('');
    setIsEndingExam(true);

    try {
      const response = await postEndExam(sessionId);

      if (response.status === 409) {
        let detail = '';
        try {
          const data = await response.json();
          detail = data?.detail || data?.message || '';
        } catch (error) {
          detail = '';
        }

        if (detail.toLowerCase().includes('ended')) {
          navigate(`/sessions/${sessionId}/exam/results`);
          return;
        }
      }

      if (!response.ok) {
        const message = await buildErrorMessage(response, 'Failed to end exam.');
        throw new Error(message);
      }

      navigate(`/sessions/${sessionId}/exam/results`);
    } catch (error) {
      setEndExamError(error.message || 'Failed to end exam.');
    } finally {
      setIsEndingExam(false);
    }
  };

  const fetchQuestionsList = useCallback(async () => {
    setPaperLoading(true);
    setPaperError(null);

    try {
      const response = await getExamQuestions(sessionId);

      if (!response.ok) {
        const message = await buildErrorMessage(response, 'Failed to load exam questions.');
        throw new Error(message);
      }

      const data = await response.json();
      setPaperQuestions(Array.isArray(data?.questions) ? data.questions : []);
    } catch (error) {
      setPaperError(error.message || 'Failed to load exam questions.');
    } finally {
      setPaperLoading(false);
    }
  }, [sessionId]);

  const fetchQuestionAndState = useCallback(
    async (targetOrderIndex) => {
      if (isEnded) {
        return;
      }

      setIsLoading(true);
      setErrorMessage('');
      setNoticeMessage('');

      try {
        const navigateResponse = await postExamNavigate(sessionId, {
          order_index: targetOrderIndex,
          create_attempt: true,
          part_label: 'a',
        });
        if (await handleGuardianVerificationBlock(navigateResponse)) {
          return;
        }

        if (navigateResponse.status === 409) {
          let detail = '';
          try {
            const data = await navigateResponse.json();
            detail = data?.detail || data?.message || '';
          } catch (error) {
            detail = '';
          }

          if (detail.includes('Session has ended')) {
            setIsEnded(true);
            return;
          }
        }

        if (navigateResponse.status === 404) {
          setQuestion(null);
          setAttemptId('');
          setWorkspaceState(null);
          setNoticeMessage('Question not found in this exam paper.');
          return;
        }

        if (!navigateResponse.ok) {
          const message = await buildErrorMessage(
            navigateResponse,
            'Failed to navigate to the question.'
          );
          throw new Error(message);
        }

        const navigateData = await navigateResponse.json();
        setQuestion(navigateData?.question ?? null);
        setAttemptId(navigateData?.attempt_id ?? '');

        const stateResponse = await getExamQuestionState(sessionId, targetOrderIndex);
        if (await handleGuardianVerificationBlock(stateResponse)) {
          return;
        }

        if (stateResponse.status === 409) {
          let detail = '';
          try {
            const data = await stateResponse.json();
            detail = data?.detail || data?.message || '';
          } catch (error) {
            detail = '';
          }

          if (detail.includes('Session has ended')) {
            setIsEnded(true);
            return;
          }
        }

        if (!stateResponse.ok) {
          const message = await buildErrorMessage(
            stateResponse,
            'Failed to load workspace state.'
          );
          throw new Error(message);
        }

        const stateData = await stateResponse.json();
        setWorkspaceState(stateData ?? null);
      } catch (error) {
        setErrorMessage(error.message || 'Failed to load the exam question.');
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, isEnded, handleGuardianVerificationBlock]
  );

  useEffect(() => {
    let isMounted = true;

    const initializeExam = async () => {
      setIsLoading(true);
      setErrorMessage('');
      setNoticeMessage('');

      try {
        const startResponse = await postStartExam(sessionId);
        if (await handleGuardianVerificationBlock(startResponse)) {
          return;
        }

        if (startResponse.status === 409) {
          let detail = '';
          try {
            const data = await startResponse.json();
            detail = data?.detail || data?.message || '';
          } catch (error) {
            detail = '';
          }

          if (detail.includes('Session has ended')) {
            if (isMounted) {
              setIsEnded(true);
            }
            return;
          }
        }

        if (!startResponse.ok) {
          const message = await buildErrorMessage(startResponse, 'Failed to start exam.');
          throw new Error(message);
        }

        if (!isEnded) {
          await fetchQuestionsList();
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'Failed to start exam.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeExam();

    return () => {
      isMounted = false;
    };
  }, [sessionId, fetchQuestionsList, handleGuardianVerificationBlock, isEnded]);

  const maxOrderIndex = useMemo(() => {
    return paperQuestions?.length
      ? Math.max(...paperQuestions.map((item) => item.order_index))
      : null;
  }, [paperQuestions]);

  useEffect(() => {
    if (isEnded) {
      return;
    }
    fetchQuestionAndState(orderIndex);
  }, [sessionId, orderIndex, fetchQuestionAndState, isEnded]);

  const statusByPartId = useMemo(() => {
    const parts = workspaceState?.parts || [];
    return new Map(parts.map((part) => [part.part_id, part]));
  }, [workspaceState]);
  const firstPartPrompt = question?.parts?.[0]?.prompt_suffix_markdown || '';
  const sharedStemVisible = shouldRenderSharedQuestionStem({
    stemMarkdown: question?.stem_markdown,
    firstPartPrompt,
  });

  const handlePrev = () => {
    setRangeMessage('');
    setOrderIndex((prev) => {
      const nextIndex = Math.max(1, prev - 1);
      setJumpValue(String(nextIndex));
      return nextIndex;
    });
  };

  const handleNext = () => {
    setRangeMessage('');
    setOrderIndex((prev) => {
      const next = prev + 1;
      const nextIndex =
        typeof maxOrderIndex === 'number' ? Math.min(next, maxOrderIndex) : next;
      setJumpValue(String(nextIndex));
      return nextIndex;
    });
  };

  const handleJump = (event) => {
    event.preventDefault();
    const parsed = Number.parseInt(jumpValue, 10);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return;
    }
    if (typeof maxOrderIndex === 'number') {
      if (parsed < 1 || parsed > maxOrderIndex) {
        setRangeMessage(`Valid range: 1–${maxOrderIndex}`);
        return;
      }
    } else {
      setRangeMessage('Paper not loaded; range unknown');
    }
    setRangeMessage('');
    setOrderIndex(parsed);
  };

  const handleSelectOrderIndex = (targetIndex) => {
    setRangeMessage('');
    setOrderIndex(targetIndex);
    setJumpValue(String(targetIndex));
  };

  return (
    <div className="page page-shell page-stack">
      <section className="card exam-hero">
        <div className="page-header">
          <div>
          <p className="eyebrow">Exam mode</p>
          <h1>{formatSessionLabel({ mode: 'EXAM' })}</h1>
          <p>Navigate the current exam paper, track question state, and end the paper when results are ready.</p>
          </div>
          {!isEnded ? <div className="exam-metric-row">
            <div className="exam-metric">
              <span className="dashboard-metric-label">Current question</span>
              <strong className="dashboard-metric-value">{orderIndex}</strong>
            </div>
            <div className="exam-metric">
              <span className="dashboard-metric-label">Paper questions</span>
              <strong className="dashboard-metric-value">{paperQuestions.length}</strong>
            </div>
            <div className="exam-metric">
              <span className="dashboard-metric-label">Attempt</span>
              <strong className="dashboard-metric-value">{attemptId || 'Pending'}</strong>
            </div>
          </div> : null}
        </div>
      </section>
      <section className="card exam-card">
      <div className="exam-question-meta">
        {!isEnded ? (
          <>
            <button type="button" className="btn btn-primary" onClick={handleEndExam} disabled={isEndingExam}>
              End exam
            </button>
            <p>Results become available after the paper is ended and graded.</p>
          </>
        ) : null}
      </div>
      {endExamError ? <p role="alert" className="notice notice-error">{endExamError}</p> : null}
      {paperLoading ? <p>Loading paper...</p> : null}
      {paperError ? <p className="notice notice-error">Paper load failed: {paperError}</p> : null}
      {isLoading ? <p>Loading...</p> : null}
      {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}
      {noticeMessage ? <p className="notice notice-info">{noticeMessage}</p> : null}
      {rangeMessage ? <p className="notice notice-info">{rangeMessage}</p> : null}
      {isEnded ? (
        <div className="stack-sm">
          <p>This exam session has ended.</p>
          <Link to={`/sessions/${sessionId}/exam/results`} className="btn btn-secondary">View results</Link>
        </div>
      ) : null}
      {!isEnded && question ? (
        <div className="exam-question-grid">
          {sharedStemVisible ? (
            <section className="card exam-question-workspace" data-testid="shared-question-stem">
              <p className="section-title">Question stem</p>
              <MarkdownRenderer content={question.stem_markdown} />
            </section>
          ) : null}
          <section className="card exam-card">
            <p className="section-title">Part state</p>
            <div className="exam-question-list">
              {(question.parts || []).map((part, partIndex) => {
                const partState = statusByPartId.get(part.id);
                const status = partState ? partState.status : 'NOT_STARTED';
                const submittedSuffix = partState?.latest_submission ? ' (submitted)' : '';
                const promptMarkdown =
                  !sharedStemVisible && partIndex === 0 && !part.prompt_suffix_markdown
                    ? question.stem_markdown
                    : part.prompt_suffix_markdown;

                return (
                  <div key={part.part_label} className="exam-part-card">
                    <div className="list-item-header">
                      <strong>Part {part.part_label}</strong>
                      <span className="status-pill status-pill-draft">
                        {formatState(status, 'student')}
                        {submittedSuffix}
                      </span>
                    </div>
                    {promptMarkdown ? (
                      <div className="markdown-block" style={{ marginTop: 'var(--space-3)' }}>
                        <MarkdownRenderer content={promptMarkdown} />
                      </div>
                    ) : (
                      <p className="muted-text" style={{ marginTop: 'var(--space-3)' }}>
                        No extra prompt text for this part.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      ) : null}
      </section>
      {!isEnded ? (
        <>
          <section className="card exam-card">
          <p className="section-title">Paper navigation</p>
          <div className="exam-nav-row">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handlePrev}
              disabled={isLoading || orderIndex <= 1}
            >
              Prev
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleNext}
              disabled={isLoading || (maxOrderIndex !== null && orderIndex >= maxOrderIndex)}
            >
              Next
            </button>
          </div>
          <form onSubmit={handleJump} className="exam-jump-row">
            <label htmlFor="orderIndexInput">Go to question</label>
            <input
              id="orderIndexInput"
              type="number"
              min="1"
              value={jumpValue}
              onChange={(event) => setJumpValue(event.target.value)}
              disabled={isLoading}
            />
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              Go
            </button>
          </form>
          <ul className="data-list">
            {paperQuestions.map((item) => (
              <li key={item.order_index} className="data-list-item">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleSelectOrderIndex(item.order_index)}
                  disabled={isLoading}
                >
                  {item.order_index} - {item.question_id} - {item.status}
                </button>
              </li>
            ))}
          </ul>
          </section>
        </>
      ) : null}
    </div>
  );
}

export default ExamSessionPage;
